// @ts-check
/** @typedef {import('../types/api').InstagramPost} InstagramPost */
/** @typedef {import('../types/api').PaginatedInstagramPosts} PaginatedInstagramPosts */
/** @typedef {import('../types/api').PostAnalysis} PostAnalysis */
/** @typedef {import('../types/api').PostInsights} PostInsights */
/** @typedef {import('../types/api').PostRecommendation} PostRecommendation */
/** @typedef {import('../types/api').ScheduledPost} ScheduledPost */
/** @typedef {import('../types/api').PaginatedScheduledPosts} PaginatedScheduledPosts */
/** @typedef {import('../types/api').Product} Product */

import api from '../utils/apiClient';
import { assertArray, assertShape } from '../utils/apiGuards';

const postMarketingService = {
  /**
   * @returns {Promise<Object>}
   */
  syncPosts: () => api.post('/api/admin/posts/sync'),

  /**
   * @param {Object} [params]
   * @returns {Promise<PaginatedInstagramPosts>}
   */
  getPosts: async ({ page, limit } = {}) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page);
    if (limit) params.append('limit', limit);
    const query = params.toString();
    const data = await api.get(`/api/admin/posts${query ? `?${query}` : ''}`);
    assertShape(data, ['items', 'total'], 'postMarketingService.getPosts');
    return data;
  },

  /**
   * @param {string} postId
   * @returns {Promise<PostAnalysis>}
   */
  analysePost: (postId) => api.post(`/api/admin/posts/${postId}/analyse`),

  /**
   * @param {string} postId
   * @returns {Promise<PostAnalysis>}
   */
  getAnalysis: async (postId) => {
    const data = await api.get(`/api/admin/posts/${postId}/analysis`);
    assertShape(data, ['post_id'], 'postMarketingService.getAnalysis');
    return data;
  },

  /**
   * @returns {Promise<PostInsights>}
   */
  getInsights: async () => {
    const data = await api.get('/api/admin/posts/insights');
    // Backend InsightsResponse has total_analysed/average_score/best_post/worst_post/
    // top_improvements — there is no "engagement" key, so asserting it threw on every
    // valid response and the Insights panel never rendered.
    assertShape(data, ['total_analysed', 'average_score'], 'postMarketingService.getInsights');
    return data;
  },

  /**
   * @param {Object} data
   * @returns {Promise<PostRecommendation>}
   */
  getRecommendation: (data) => api.post('/api/admin/posts/recommend', data),

  /**
   * @param {string} postId
   * @param {string[]} productIds
   * @returns {Promise<Object>}
   */
  tagProducts: (postId, productIds) =>
    api.patch(`/api/admin/posts/${postId}/tag-products`, { product_ids: productIds }),

  /**
   * @returns {Promise<Product[]>}
   */
  getProducts: async () => {
    const data = await api.get('/api/admin/products/');
    assertShape(data, ['items', 'total'], 'postMarketingService.getProducts');
    return data;
  },

  /**
   * @param {string} postId
   * @param {string} productId
   * @returns {Promise<Object>}
   */
  linkProduct: (postId, productId) => api.patch(`/api/admin/posts/${postId}/link-product`, { product_id: productId }),

  /**
   * @param {string} productId
   * @returns {Promise<InstagramPost[]>}
   */
  getPostsByProduct: async (productId) => {
    const data = await api.get(`/api/admin/posts/by-product/${productId}`);
    assertArray(data, 'postMarketingService.getPostsByProduct');
    return data;
  },

  /**
   * @param {Object} [params]
   * @returns {Promise<PaginatedScheduledPosts>}
   */
  getScheduled: async ({ page, limit } = {}) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page);
    if (limit) params.append('limit', limit);
    const query = params.toString();
    const data = await api.get(`/api/admin/posts/scheduled${query ? `?${query}` : ''}`);
    assertShape(data, ['items', 'total'], 'postMarketingService.getScheduled');
    return data;
  },

  /**
   * @param {Object} data
   * @returns {Promise<ScheduledPost>}
   */
  createScheduled: (data) => api.post('/api/admin/posts/scheduled', data),

  /**
   * @param {string} id
   * @param {Object} data
   * @returns {Promise<ScheduledPost>}
   */
  updateScheduled: (id, data) => api.put(`/api/admin/posts/scheduled/${id}`, data),

  /**
   * @param {string} id
   * @returns {Promise<void>}
   */
  deleteScheduled: (id) => api.del(`/api/admin/posts/scheduled/${id}`),

  /**
   * @param {string} id
   * @returns {Promise<ScheduledPost>}
   */
  publishScheduled: (id) => api.patch(`/api/admin/posts/scheduled/${id}/publish`),
};

export default postMarketingService;
